import React, { useState } from 'react';
import { Upload, message, Icon, Button, Spin, Input } from 'antd';
import client from '../../../api/graphql/client';
import { showSuccess, showError } from '../../common/notifications';
import { uploadFileToAzureBlob } from '../../common';
import { GET_USER_TIMESHEET } from '../queries';
import { UPLOAD_TIMESHEET_EXPENSE, DELETE_TIMESHEET_EXPENSE } from '../mutations';
import moment from 'moment';
import AttachmentLink from './AttachmentLink';
import { useGlobal } from 'reactn';

const { TextArea } = Input;

const TimesheetExpense = ({ timesheet, endDate, readonly }) => {
  const { timesheetExpense, startDate } = timesheet;
  const [user] = useGlobal('user');
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState(
    timesheetExpense?.description
      ? timesheetExpense.description
      : `${user.firstName?.toLowerCase()}'s ${moment(startDate)
          .format('MMMM YYYY')
          .toLowerCase()} expenses`,
  );
  const [amount, setAmount] = useState(timesheetExpense?.amount ? timesheetExpense.amount : 0);
  const beforeUpload = file => {
    const isLt20M = file.size / 1024 / 1024 < 20;
    if (!isLt20M) {
      message.error('Image must smaller than 20MB!');
    }
    return isLt20M;
  };
  const uploadButton = uploading ? (
    <Button>
      <Spin style={{ padding: '5px' }} />
      <span style={{ padding: '5px' }}>Uploading..</span>
    </Button>
  ) : (
    <Button>
      <Icon type="upload" /> Upload Attachments
    </Button>
  );
  const saveExpense = () => {
    client
      .mutate({
        mutation: UPLOAD_TIMESHEET_EXPENSE,
        variables: {
          timeSheetId: timesheet._id,
          description: description,
          amount: parseFloat(amount),
        },
        refetchQueries: [
          {
            query: GET_USER_TIMESHEET,
            variables: { endDate },
          },
        ],
      })
      .then(() => {
        showSuccess({ message: 'Updated Successfully' });
        setUploading(false);
      })
      .catch(err => showError({ message: err.message }));
  };
  const uploadHandler = async ({ file }) => {
    setUploading(true);
    const { name } = file;
    const fileName = `${Date.now()}_${name}`;
    const url = await uploadFileToAzureBlob({
      fileName,
      file,
      blobContainerName: 'uploads',
    });
    client
      .mutate({
        mutation: UPLOAD_TIMESHEET_EXPENSE,
        variables: {
          timeSheetId: timesheet._id,
          url: url,
        },
        refetchQueries: [
          {
            query: GET_USER_TIMESHEET,
            variables: { endDate },
          },
        ],
      })
      .then(() => {
        showSuccess({ message: 'Updated Successfully' });
        setUploading(false);
      })
      .catch(err => showError({ message: err.message }));
  };

  const deleteHandler = id => {
    client
      .mutate({
        mutation: DELETE_TIMESHEET_EXPENSE,
        variables: {
          timeSheetId: timesheet._id,
          id: id,
        },
        refetchQueries: [
          {
            query: GET_USER_TIMESHEET,
            variables: { endDate },
          },
        ],
      })
      .then(() => {
        showSuccess({ message: 'Deleted Successfully' });
        setUploading(false);
      })
      .catch(err => showError({ message: err.message }));
  };

  return (
    <div className="p-4">
      {!readonly ? (
        <div className="flex flex-col gap-4">
          <div
            style={{
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#262626',
                lineHeight: '1.8',
              }}
            >
              <div style={{ fontWeight: '500', marginBottom: '8px', color: '#1890ff' }}>
                <Icon type="info-circle" style={{ marginRight: '8px' }} />
                Instructions:
              </div>
              <ol style={{ paddingLeft: '24px', margin: 0 }}>
                <li>Please input a brief description of your expenses and the amount.</li>
                <li>
                  Please upload your populated Spruce expenses XLS template if any expenses
                  incurred.
                </li>
                <li>Please upload all your receipts in a zipped file.</li>
              </ol>
            </div>
          </div>
          <div
            style={{
              background: '#fafafa',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '16px',
            }}
          >
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#262626',
                }}
              >
                Brief Description
              </label>
              <TextArea
                name="description"
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                }}
                placeholder="Enter description..."
                rows={3}
                style={{
                  width: '100%',
                  resize: 'vertical',
                  fontSize: '14px',
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#262626',
                }}
              >
                Amount
              </label>
              <Input
                name="amount"
                type="number"
                value={amount}
                onChange={e => {
                  setAmount(e.target.value);
                }}
                placeholder="0.00"
                prefix="$"
                style={{ fontSize: '14px' }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <Upload
                name="proof"
                customRequest={value => uploadHandler(value)}
                beforeUpload={beforeUpload}
                multiple={false}
                showUploadList={false}
                disabled={uploading}
              >
                {uploadButton}
              </Upload>
              <Button type="primary" onClick={saveExpense}>
                Save
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            background: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <span style={{ fontWeight: '600', color: '#595959', fontSize: '14px' }}>
              Description:{' '}
            </span>
            <span style={{ color: '#262626', fontSize: '14px' }}>{description}</span>
          </div>
          <div>
            <span style={{ fontWeight: '600', color: '#595959', fontSize: '14px' }}>Amount: </span>
            <span style={{ color: '#262626', fontSize: '14px' }}>${amount}</span>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {timesheetExpense &&
          timesheetExpense.attachments?.map(item => {
            return (
              <div
                key={item._id}
                style={{
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  padding: '16px 20px',
                  background: '#fff',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = '#e8e8e8';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon
                        type="paper-clip"
                        style={{ color: '#1890ff', fontSize: '16px', flexShrink: 0 }}
                      />
                      <AttachmentLink url={item.url} />
                    </div>
                  </div>
                  {timesheet.status === 'Not Submitted' && !readonly && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        deleteHandler(item._id);
                      }}
                      style={{
                        padding: '4px',
                        flexShrink: 0,
                        color: '#ff4d4f',
                        alignSelf: 'flex-start',
                        marginTop: '-4px',
                      }}
                    >
                      <Icon type="delete" style={{ color: '#ff4d4f' }} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
      </div>
      {timesheetExpense &&
        (!timesheetExpense.attachments || timesheetExpense.attachments.length === 0) &&
        readonly && (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 20px',
              color: '#8c8c8c',
              fontSize: '14px',
              background: '#fafafa',
              borderRadius: '8px',
              border: '1px dashed #d9d9d9',
            }}
          >
            <Icon
              type="inbox"
              style={{ fontSize: '32px', marginBottom: '8px', color: '#bfbfbf' }}
            />
            <div>No Attachments</div>
          </div>
        )}
    </div>
  );
};

export default TimesheetExpense;
